import { PLATFORM_ID, inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { catchError, from, Observable, switchMap, throwError, EMPTY, of, firstValueFrom, take } from 'rxjs';

import { environment } from '../environments/environment';
import { AuthApis } from '../services/authApis';

import { selectIsLoggedIn, selectUserName, selectToken, selectUserDetail, selectSessionId } from '../app/store/user/user.selector';

import { clearUser } from '../app/store/user/user.actions';

let refreshInProgress = false;
let logoutInProgress = false;

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<any>, next: HttpHandlerFn) => {
  const platformId = inject(PLATFORM_ID);
  const router = inject(Router);
  const store = inject(Store);
  const authApis = inject(AuthApis);

  if (!isPlatformBrowser(platformId)) return next(req);

  const skipAuth = req.headers.has('x-skip-auth');
  if (skipAuth) {
    const cleanReq = req.clone({
      headers: req.headers.delete('x-skip-auth'),
    });
    return next(cleanReq);
  }

  const isPublicEndpoint = req.url.endsWith('/status') || req.url.includes('auth/login') || req.url.includes('auth/get-session');

  const isMicroserviceWithoutSession = req.url.includes(environment.microServiceLocationUrl) || req.url.includes(environment.microServiceCustomerUrl);

  return from(readAuthSnapshot(store)).pipe(
    switchMap(({ isLoggedIn, userName, sessionId }) => {
      if (!isLoggedIn && !isPublicEndpoint) {
        router.navigate(['/auth/login'], { replaceUrl: true });
        return EMPTY;
      }

      return from(authApis.getValidAccessToken()).pipe(
        switchMap((token) => {
          const headers: Record<string, string> = {};

          if (token) headers['Authorization'] = `Bearer ${token}`;
          if (sessionId && !isMicroserviceWithoutSession) {
            headers['X-Session-Id'] = sessionId;
          }

          const authReq = req.clone({ setHeaders: headers });
          return next(authReq);
        })
      );
    }),

    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isPublicEndpoint) {
        return handle401AndRetry(req, next, store, authApis, router);
      }

      return throwError(() => error);
    })
  );
};

async function readAuthSnapshot(store: Store): Promise<{
  isLoggedIn: boolean;
  userName: string;
  sessionId: string | null;
}> {
  const isLoggedIn = await firstValue(store.select(selectIsLoggedIn), false);
  const userName = (await firstValue(store.select(selectUserName), '')) ?? '';
  const sessionId = (await firstValue(store.select(selectSessionId), '')) ?? '';

  return { isLoggedIn, userName, sessionId };
}

function isNetworkError(err: any): boolean {
  if (!err) return false;
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  const msg = (err.message || '').toLowerCase();
  const name = (err.name || '').toLowerCase();
  return (
    msg.includes('network') ||
    msg.includes('fetch') ||
    msg.includes('timeout') ||
    msg.includes('dns') ||
    msg.includes('load failed') ||
    msg.includes('failed to fetch') ||
    msg.includes('connection') ||
    msg.includes('offline') ||
    name.includes('network') ||
    name.includes('timeout')
  );
}

function buildRetryHeaders(token: string, sessionId: string | null, req: HttpRequest<any>): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
  const isMicroserviceWithoutSession = req.url.includes(environment.microServiceLocationUrl) || req.url.includes(environment.microServiceCustomerUrl);
  if (sessionId && !isMicroserviceWithoutSession) {
    headers['X-Session-Id'] = sessionId;
  }
  return headers;
}

function handle401AndRetry(req: HttpRequest<any>, next: HttpHandlerFn, store: Store, authApis: AuthApis, router: Router): Observable<any> {
  return from(firstValue(store.select(selectSessionId), '')).pipe(
    switchMap((sessionId) => {
      if (refreshInProgress) {
        return from(authApis.getValidAccessToken()).pipe(
          switchMap((token) => {
            if (!token) return logoutAndRedirect(store, authApis, router);

            const retryReq = req.clone({
              setHeaders: buildRetryHeaders(token, sessionId, req),
            });
            return next(retryReq);
          }),
          catchError((err) => throwError(() => err))
        );
      }

      refreshInProgress = true;

      return from(authApis.refreshTokens()).pipe(
        switchMap((refreshed) => {
          refreshInProgress = false;

          if (refreshed === 'NETWORK_ERROR') {
            return throwError(() => new Error('Network error during token refresh'));
          }

          const newToken = refreshed && typeof refreshed === 'object' ? refreshed.accessToken : undefined;
          if (!newToken) {
            return logoutAndRedirect(store, authApis, router);
          }

          const retryReq = req.clone({
            setHeaders: buildRetryHeaders(newToken, sessionId, req),
          });
          return next(retryReq);
        }),
        catchError((err) => {
          refreshInProgress = false;
          if (isNetworkError(err)) {
            return throwError(() => err);
          }
          return logoutAndRedirect(store, authApis, router);
        })
      );
    })
  );
}

function logoutAndRedirect(store: Store, authApis: AuthApis, router: Router) {
  if (logoutInProgress) return EMPTY;
  logoutInProgress = true;

  // Reset the whole user slice (isLoggedIn/userName/detail/permissions/session/tokens) in one go.
  store.dispatch(clearUser());

  authApis.logout();

  router.navigate(['/auth/login'], { replaceUrl: true }).finally(() => {
    logoutInProgress = false;
  });

  return EMPTY;
}

async function firstValue<T>(obs$: Observable<T>, fallback: T): Promise<T> {
  try {
    const value = await firstValueFrom(obs$.pipe(take(1)));
    return (value ?? fallback) as T;
  } catch {
    return fallback;
  }
}
